import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Every AI ACTION control in the app. When the active tenant is locked (a Free
// Trial that has reached Day 0 / expired, or a suspended account) these are
// greyed out and un-clickable everywhere — the backend also refuses them, this
// makes it visually clear the action isn't available. Add new AI buttons here.
// NOTE: deliberately excludes look-alikes that are NOT AI (e.g.
// onboard-generate-password, cip004-link-generate, reset/force-reset-generate).
const AI_TESTIDS = [
  "generate-all-ai-btn", "generate-all-defs-btn", "generate-all-sme-btn",
  "generate-narrative-btn", "generate-policy-btn", "generate-procedure-btn",
  "generate-sop-btn", "generate-form-btn", "checklist-generate-btn",
  "flow-generate-btn", "rsaw-generate-all-btn", "rsaw-draft-parts-btn",
  "star-generate-narratives-btn", "scrm-ai-research-btn", "risk-ai-seed-btn",
  "ira-suggest-all-btn", "ai-suggest-questions", "ai-suggest-evidence-btn",
  "suggest-refs-btn", "sme-editor-generate", "rfi-ai-answer",
  "da-analyze-btn", "devstar-analyze-btn", "analyze-btn",
  // ClaroLens AI actions (ClaroLensDetail also gates these via read-only)
  "rr-generate-all", "rr-import-btn", "rr-rsaw-btn", "rr-apply-all-rsaw",
];

const SELECTOR = AI_TESTIDS.map((t) => `[data-testid="${t}"]`).join(",");

export const AiLockGuard = () => {
  const { user } = useAuth();
  const [locked, setLocked] = useState(false);

  const check = useCallback(async () => {
    if (!user) { setLocked(false); return; }
    try {
      const { data } = await api.get("/tenant/me", { __silent402: true });
      setLocked(!!data?.locked);
    } catch { setLocked(false); }
  }, [user]);

  useEffect(() => {
    check();
    const h = () => check();
    window.addEventListener("claro:tenant-updated", h);
    window.addEventListener("claro:registrations-changed", h);
    return () => {
      window.removeEventListener("claro:tenant-updated", h);
      window.removeEventListener("claro:registrations-changed", h);
    };
  }, [check]);

  useEffect(() => {
    if (!locked) return;

    // 1) Grey out + block pointer events via an injected stylesheet.
    const style = document.createElement("style");
    style.setAttribute("data-ai-lock", "1");
    style.innerHTML = `${SELECTOR} {
      opacity: .45 !important;
      filter: grayscale(1) !important;
      pointer-events: none !important;
      cursor: not-allowed !important;
    }`;
    document.head.appendChild(style);

    // 2) Semantic disable on matched controls, re-applied as the SPA re-renders.
    const apply = () => {
      document.querySelectorAll(SELECTOR).forEach((el) => {
        el.setAttribute("aria-disabled", "true");
        if (el.tagName === "BUTTON" || el.tagName === "INPUT") el.disabled = true;
      });
    };
    apply();
    const obs = new MutationObserver(() => apply());
    obs.observe(document.body, { childList: true, subtree: true });

    // 3) Capture-phase backstop: even if a style is overridden, a click on an AI
    //    control is cancelled and the user is told why.
    const onClick = (e) => {
      const el = e.target?.closest?.(SELECTOR);
      if (el) {
        e.preventDefault();
        e.stopPropagation();
        toast.error("Your Free Trial has ended — AI features are disabled. Upgrade to continue.");
      }
    };
    document.addEventListener("click", onClick, true);

    return () => {
      style.remove();
      obs.disconnect();
      document.removeEventListener("click", onClick, true);
    };
  }, [locked]);

  return null;
};

export default AiLockGuard;
